import { create } from 'zustand';

import { STORAGE_KEYS } from '@config/storageKeys';
import { loadJson, saveJson } from '@lib/storage';
import { ensureAccountsParticulars, recalcAccountTotals } from '@lib/accounts';
import { mergeComponent3, isValidComponent3, c3RecalcDeliverable, c3RecalcExpense } from '@lib/c3';
import { normalizeGapStatus } from '@lib/gap';
import {
  findCmRowForProc,
  findCmRowIndex,
  mergeCmRow,
  stripCmRowMeta,
  PROC_STATUS,
  appendProcHistory,
  buildCmRowFromProc,
  normalizeProcRow,
  procCmCategory,
  procHistoryActor,
} from '@lib/procurement';
import { enrichCoeTrainingRows, enrichMarketTrainingRows, enrichTrainingPrincipals } from '@lib/training';
import { normalizePlacementEmploymentStatus } from '@lib/trainingPlacementImportExport';
import type {
  AccountsState,
  AppStore,
  CivilFinancialState,
  CivilRow,
  GapAlert,
  GapRow,
  ProcListKey,
  ProcRow,
  TrainingRow,
  C1PlacementRow,
} from '@type/store';
import { SEED } from '@/data';

const DATA_VERSION = 18;
const LEGACY_GAP_STATUSES = ['Pending', 'Yet To Initiate'];

/** Read once at startup — must not be mutated before loaders run. */
const STORED_VERSION = loadJson<number | null>(STORAGE_KEYS.dataVersion, null);

function isValidTrainingRows(_component: number, rows: unknown): rows is TrainingRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((r) => r && (r.course || r.inst || r.div || r.dist));
}

function loadTraining(): Record<number, TrainingRow[]> {
  const result: Record<number, TrainingRow[]> = {
    1: [...SEED.TRAINING_BY_COMPONENT[1]],
    2: [...SEED.TRAINING_BY_COMPONENT[2]],
    3: [],
  };
  if (STORED_VERSION === DATA_VERSION) {
    [1, 2].forEach((c) => {
      const saved = loadJson(STORAGE_KEYS.training(c), null);
      if (isValidTrainingRows(c, saved)) result[c] = saved;
    });
  }
  result[1] = enrichCoeTrainingRows(enrichTrainingPrincipals(result[1], SEED.TRAINING_PRINCIPALS));
  result[2] = enrichMarketTrainingRows(enrichTrainingPrincipals(result[2], SEED.TRAINING_PRINCIPALS));
  return result;
}

function normalizePlacementRecord(row: C1PlacementRow, index = 0): C1PlacementRow {
  const employmentStatus = normalizePlacementEmploymentStatus(row.employmentStatus);
  return {
    ...row,
    sr: row.sr ?? index + 1,
    employmentStatus: employmentStatus || row.employmentStatus || '',
  };
}

function loadC1Placement(): C1PlacementRow[] {
  const saved = loadJson<C1PlacementRow[] | null>(STORAGE_KEYS.c1Placement, null);
  return Array.isArray(saved) ? saved.map((row, i) => normalizePlacementRecord(row, i)) : [];
}

function loadC2Placement(): C1PlacementRow[] {
  const saved = loadJson<C1PlacementRow[] | null>(STORAGE_KEYS.c2Placement, null);
  return Array.isArray(saved) ? saved.map((row, i) => normalizePlacementRecord(row, i)) : [];
}

function loadCivilFinancial(): CivilFinancialState {
  const saved = loadJson<CivilFinancialState | null>(STORAGE_KEYS.civilFinancial, null);
  if (saved && typeof saved === 'object') {
    return {
      portfolio: Array.isArray(saved.portfolio) ? saved.portfolio : [],
      bySite: saved.bySite && typeof saved.bySite === 'object' ? saved.bySite : {},
    };
  }
  return { portfolio: [], bySite: {} };
}

function loadCivil(): CivilRow[] {
  const saved = loadJson<CivilRow[] | null>(STORAGE_KEYS.civil, null);
  if (STORED_VERSION === DATA_VERSION && Array.isArray(saved) && saved.length === SEED.CIVIL.length) {
    return saved;
  }
  return [...SEED.CIVIL];
}

function loadComponent3(): Record<string, unknown> {
  if (STORED_VERSION !== DATA_VERSION) return mergeComponent3(null);
  const saved = loadJson(STORAGE_KEYS.component3, null);
  const merged = mergeComponent3(saved);
  return isValidComponent3(merged) ? merged : mergeComponent3(null);
}

function loadProcYetToInitiate(): ProcRow[] {
  const saved = loadJson<ProcRow[] | null>(STORAGE_KEYS.procYetToInitiate, null);
  if (STORED_VERSION === DATA_VERSION && Array.isArray(saved) && saved.length > 0) {
    return saved.map(normalizeProcRow);
  }
  return SEED.PROC_YET_TO_INITIATE.map(normalizeProcRow);
}

function loadProcArray(key: string, seed: ProcRow[]): ProcRow[] {
  const saved = loadJson<ProcRow[] | null>(key, null);
  if (STORED_VERSION === DATA_VERSION && Array.isArray(saved)) return saved.map(normalizeProcRow);
  return seed.map(normalizeProcRow);
}

function loadProcHistory() {
  const saved = loadJson(STORAGE_KEYS.procHistory, null);
  if (STORED_VERSION === DATA_VERSION && Array.isArray(saved)) return saved;
  return [];
}

function loadAccounts(): AccountsState {
  const saved = loadJson<AccountsState | null>(STORAGE_KEYS.accounts, null);
  if (STORED_VERSION === DATA_VERSION && saved?.accountsByYear) {
    return ensureAccountsParticulars(saved);
  }
  return ensureAccountsParticulars(JSON.parse(JSON.stringify(SEED.ACCOUNTS)) as AccountsState);
}

function isValidGapRows(rows: unknown): rows is GapRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  if (!rows.every((r) => r && typeof (r as GapRow).ind === 'string' && 'status' in r)) return false;
  return !rows.some((r) => LEGACY_GAP_STATUSES.includes(String((r as GapRow).status)));
}

function loadGap(): GapRow[] {
  const saved = loadJson<GapRow[] | null>(STORAGE_KEYS.gap, null);
  const fromSaved = STORED_VERSION === DATA_VERSION && isValidGapRows(saved);
  const rows = fromSaved ? saved : SEED.GAP;
  return rows.map((g) => ({ ...g, status: normalizeGapStatus(g.status) }));
}

function loadGapAlert(): GapAlert {
  const saved = loadJson<GapAlert | null>(STORAGE_KEYS.gapAlert, null);
  if (STORED_VERSION === DATA_VERSION && saved && typeof saved.message === 'string') {
    return saved;
  }
  return { ...SEED.GAP_ALERT };
}

function clearStaleStorage() {
  try {
    localStorage.removeItem(STORAGE_KEYS.training(1));
    localStorage.removeItem(STORAGE_KEYS.training(2));
    localStorage.removeItem(STORAGE_KEYS.component3);
    localStorage.removeItem(STORAGE_KEYS.civil);
    localStorage.removeItem(STORAGE_KEYS.civilFinancial);
    localStorage.removeItem(STORAGE_KEYS.coeOperational);
    localStorage.removeItem(STORAGE_KEYS.trainingLegacy);
    localStorage.removeItem(STORAGE_KEYS.c1Placement);
    localStorage.removeItem(STORAGE_KEYS.c2Placement);
    localStorage.removeItem(STORAGE_KEYS.gap);
    localStorage.removeItem(STORAGE_KEYS.gapAlert);
    localStorage.removeItem(STORAGE_KEYS.procYetToInitiate);
    localStorage.removeItem(STORAGE_KEYS.procInprocess);
    localStorage.removeItem(STORAGE_KEYS.procOngoing);
    localStorage.removeItem(STORAGE_KEYS.procCompleted);
    localStorage.removeItem(STORAGE_KEYS.procCivil);
    localStorage.removeItem(STORAGE_KEYS.procHistory);
    localStorage.removeItem(STORAGE_KEYS.accounts);
    localStorage.removeItem(STORAGE_KEYS.contractMgmt);
    localStorage.removeItem(STORAGE_KEYS.budget);
    localStorage.removeItem(STORAGE_KEYS.audit);
    localStorage.removeItem('tvet_coe_pictures_v1');
  } catch {
    /* ignore */
  }
}

if (STORED_VERSION !== DATA_VERSION) {
  clearStaleStorage();
  saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
}

const initialState = {
  civil: loadCivil(),
  civilFinancial: loadCivilFinancial(),
  coeOperational: loadJson(STORAGE_KEYS.coeOperational, SEED.COE_OPERATIONAL),
  procCompleted: loadProcArray(STORAGE_KEYS.procCompleted, SEED.PROC_COMPLETED),
  procOngoing: loadProcArray(STORAGE_KEYS.procOngoing, SEED.PROC_ONGOING),
  procCivil: loadProcArray(STORAGE_KEYS.procCivil, SEED.PROC_CIVIL),
  procInprocess: loadProcArray(STORAGE_KEYS.procInprocess, SEED.PROC_INPROCESS),
  procYetToInitiate: loadProcYetToInitiate(),
  procHistory: loadProcHistory(),
  contractMgmt: loadJson(STORAGE_KEYS.contractMgmt, SEED.CM),
  trainingByComponent: loadTraining(),
  c1PlacementRecords: loadC1Placement(),
  c2PlacementRecords: loadC2Placement(),
  component3: loadComponent3(),
  gap: loadGap(),
  gapAlert: loadGapAlert(),
  budgetRows: loadJson(STORAGE_KEYS.budget, SEED.BUDGET_ROWS),
  accounts: loadAccounts(),
  accountsTab: 'dashboard',
  auditLog: loadJson(STORAGE_KEYS.audit, []),
  activeModule: 'dashboard',
  selectedKpiCard: 'civil',
  civReg: 'all',
  coeTabFilter: 'all',
  procModTab: 'all',
  procModSearch: '',
  procModType: '',
  procModTypes: [],
  procModYear: '',
  procModMonth: '',
  activeTrainingComponent: 2,
  dashboardTrainingComponent: 2,
  activeC3Tab: 'deliverable-details',
  activeC3Section: 'competitive',
  c3DetailStatusFilter: 'all',
  c3PaymentStatusFilter: 'all',
  timeFilter: '1 YEAR',
  modal: null,
};

export const useAppStore = create<AppStore>((set, get) => ({
  ...initialState,

  setActiveModule: (id) => set({ activeModule: id }),
  setSelectedKpiCard: (id) => set({ selectedKpiCard: id }),
  setCivReg: (reg) => set({ civReg: reg }),
  setCoeTabFilter: (f) => set({ coeTabFilter: f }),
  setProcModTab: (tab) => set({ procModTab: tab }),
  setProcModSearch: (q) => set({ procModSearch: q }),
  setProcModType: (t) => set({ procModType: t }),
  setProcModTypes: (types) => set({ procModTypes: types }),
  setProcModYear: (year) => set({ procModYear: year }),
  setProcModMonth: (month) => set({ procModMonth: month }),
  setActiveTrainingComponent: (c) => {
    const prev = get().activeTrainingComponent;
    set({ activeTrainingComponent: c });
    if (c === 3 && prev !== 3) {
      set({ activeC3Tab: 'deliverable-details', activeC3Section: 'competitive', c3DetailStatusFilter: 'all', c3PaymentStatusFilter: 'all' });
    }
  },
  setDashboardTrainingComponent: (c) => {
    const prev = get().dashboardTrainingComponent;
    set({ dashboardTrainingComponent: c });
    if (c === 3 && prev !== 3) {
      set({ activeC3Tab: 'deliverable-details', activeC3Section: 'competitive', c3DetailStatusFilter: 'all', c3PaymentStatusFilter: 'all' });
    }
  },
  setActiveC3Tab: (tab) => set({ activeC3Tab: tab }),
  setActiveC3Section: (section) => {
    const nextTab = section === 'non-competitive' ? 'provisional-sums' : 'deliverable-details';
    set({ activeC3Section: section, activeC3Tab: nextTab });
  },
  setC3DetailStatusFilter: (filter) => set({ c3DetailStatusFilter: filter }),
  setC3PaymentStatusFilter: (filter) => set({ c3PaymentStatusFilter: filter }),
  setTimeFilter: (f) => set({ timeFilter: f }),

  resetTrainingSeed: () => {
    clearStaleStorage();
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({
      trainingByComponent: loadTraining(),
      component3: mergeComponent3(null),
      activeC3Tab: 'deliverable-details',
      c3DetailStatusFilter: 'all',
      c3PaymentStatusFilter: 'all',
    });
  },

  ensureTrainingData: () => {
    const { trainingByComponent, component3, resetTrainingSeed } = get();
    const badC1 = !Array.isArray(trainingByComponent[1]) || trainingByComponent[1].length === 0
      || !isValidTrainingRows(1, trainingByComponent[1]);
    const badC2 = !Array.isArray(trainingByComponent[2]) || trainingByComponent[2].length === 0
      || !isValidTrainingRows(2, trainingByComponent[2]);
    const badC3 = !isValidComponent3(component3);
    if (badC1 || badC2 || badC3) {
      resetTrainingSeed();
      return;
    }
    set({
      trainingByComponent: {
        ...trainingByComponent,
        1: enrichCoeTrainingRows(enrichTrainingPrincipals(trainingByComponent[1], SEED.TRAINING_PRINCIPALS)),
        2: enrichMarketTrainingRows(enrichTrainingPrincipals(trainingByComponent[2], SEED.TRAINING_PRINCIPALS)),
      },
    });
  },

  updateCivil: (index, row) => {
    const civil = [...get().civil];
    civil[index] = { ...civil[index], ...row };
    saveJson(STORAGE_KEYS.civil, civil);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ civil });
  },

  importCivilFinancialMonthly: (scope, rows) => {
    const civilFinancial = {
      portfolio: [...get().civilFinancial.portfolio],
      bySite: { ...get().civilFinancial.bySite },
    };
    if (scope === 'portfolio') {
      civilFinancial.portfolio = rows;
    } else {
      civilFinancial.bySite[scope] = rows;
    }
    saveJson(STORAGE_KEYS.civilFinancial, civilFinancial);
    get().addAudit(
      'Civil Works Import',
      `${scope === 'portfolio' ? 'Portfolio' : scope}: ${rows.length} monthly financial rows imported`,
    );
    set({ civilFinancial });
    return rows.length;
  },

  updateCoeOperational: (index, row) => {
    const coeOperational = [...get().coeOperational];
    coeOperational[index] = { ...coeOperational[index], ...row };
    saveJson(STORAGE_KEYS.coeOperational, coeOperational);
    set({ coeOperational });
  },

  addProcPackage: (status, row) => {
    const key: ProcListKey =
      status === 'Completed'
        ? 'procCompleted'
        : status === 'On Going'
          ? 'procOngoing'
          : status === 'Civil Works'
            ? 'procCivil'
            : status === 'Yet to Initiate'
              ? 'procYetToInitiate'
              : 'procInprocess';
    const normalized = normalizeProcRow({ ...row, status: status === 'Civil Works' ? 'On Going' : status });
    const next = [...get()[key], normalized];
    const storageKeyMap: Record<ProcListKey, string> = {
      procCompleted: STORAGE_KEYS.procCompleted,
      procOngoing: STORAGE_KEYS.procOngoing,
      procCivil: STORAGE_KEYS.procCivil,
      procInprocess: STORAGE_KEYS.procInprocess,
      procYetToInitiate: STORAGE_KEYS.procYetToInitiate,
    };
    const storageKey = storageKeyMap[key];
    if (storageKey) saveJson(storageKey, next);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ [key]: next });
  },

  pushProcHistory: (entry) => {
    const procHistory = [{ at: new Date().toLocaleString(), ...entry }, ...get().procHistory].slice(0, 200);
    saveJson(STORAGE_KEYS.procHistory, procHistory);
    set({ procHistory });
  },

  saveProcYetToInitiate: (index, row, actor) => {
    const procYetToInitiate = [...get().procYetToInitiate];
    const prev = procYetToInitiate[index];
    if (!prev) return;
    procYetToInitiate[index] = normalizeProcRow({ ...prev, ...row, status: PROC_STATUS.YET });
    saveJson(STORAGE_KEYS.procYetToInitiate, procYetToInitiate);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    get().pushProcHistory({
      action: 'Updated Yet to Initiate',
      packageId: prev.id,
      ref: procYetToInitiate[index].ref,
      ...procHistoryActor(actor),
      from: PROC_STATUS.YET,
      to: PROC_STATUS.YET,
    });
    set({ procYetToInitiate });
  },

  deleteProcYetToInitiate: (index, actor) => {
    const procYetToInitiate = [...get().procYetToInitiate];
    const prev = procYetToInitiate[index];
    if (!prev) return;
    procYetToInitiate.splice(index, 1);
    saveJson(STORAGE_KEYS.procYetToInitiate, procYetToInitiate);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    get().pushProcHistory({
      action: 'Deleted Yet to Initiate',
      packageId: prev.id,
      ref: prev.ref,
      type: prev.type,
      ...procHistoryActor(actor),
      from: PROC_STATUS.YET,
      to: 'Deleted',
    });
    get().addAudit('Procurement', `${prev.ref} deleted from Yet to Initiate`);
    set({ procYetToInitiate });
  },

  promoteYtiToInProcess: (id, updates, actor) => {
    const procYetToInitiate = [...get().procYetToInitiate];
    const idx = procYetToInitiate.findIndex((r) => r.id === id);
    if (idx < 0) return false;
    const prev = procYetToInitiate[idx];
    const moved = appendProcHistory(
      normalizeProcRow({
        ...prev,
        ...updates,
        status: PROC_STATUS.IN_PROCESS,
        initiatedBy: actor?.email || '',
        initiatedAt: new Date().toLocaleString(),
      }),
      {
        action: 'Promoted to In Process',
        ...procHistoryActor(actor),
        from: PROC_STATUS.YET,
        to: PROC_STATUS.IN_PROCESS,
      },
    );
    procYetToInitiate.splice(idx, 1);
    const procInprocess = [...get().procInprocess, moved];
    saveJson(STORAGE_KEYS.procYetToInitiate, procYetToInitiate);
    saveJson(STORAGE_KEYS.procInprocess, procInprocess);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    get().pushProcHistory({
      action: 'Promoted to In Process',
      packageId: prev.id,
      ref: moved.ref,
      type: moved.type,
      ...procHistoryActor(actor),
      from: PROC_STATUS.YET,
      to: PROC_STATUS.IN_PROCESS,
    });
    get().addAudit('Procurement', `${moved.ref} moved to In Process by ${actor?.name || 'officer'}`);
    set({ procYetToInitiate, procInprocess });
    return true;
  },

  demoteInProcessToYti: (id, actor) => {
    const procInprocess = [...get().procInprocess];
    const idx = procInprocess.findIndex((r) => r.id === id);
    if (idx < 0) return false;
    const prev = procInprocess[idx];
    const reverted = appendProcHistory(
      normalizeProcRow({
        ...prev,
        status: PROC_STATUS.YET,
        currentStatus: PROC_STATUS.YET,
      }),
      {
        action: 'Reverted to Yet to Initiate',
        ...procHistoryActor(actor),
        from: PROC_STATUS.IN_PROCESS,
        to: PROC_STATUS.YET,
      },
    );
    procInprocess.splice(idx, 1);
    const procYetToInitiate = [...get().procYetToInitiate.filter((r) => r.id !== reverted.id), reverted];
    saveJson(STORAGE_KEYS.procInprocess, procInprocess);
    saveJson(STORAGE_KEYS.procYetToInitiate, procYetToInitiate);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    get().pushProcHistory({
      action: 'Reverted to Yet to Initiate',
      packageId: prev.id,
      ref: reverted.ref,
      type: reverted.type,
      ...procHistoryActor(actor),
      from: PROC_STATUS.IN_PROCESS,
      to: PROC_STATUS.YET,
    });
    get().addAudit('Procurement', `${reverted.ref} reverted to Yet to Initiate`);
    set({ procInprocess, procYetToInitiate });
    return true;
  },

  saveProcInprocess: (index, row, actor) => {
    const procInprocess = [...get().procInprocess];
    let idx = index;
    if ((idx == null || idx < 0) && row?.id) {
      idx = procInprocess.findIndex((r) => r.id === row.id);
    }
    const prev = idx != null && idx >= 0 ? procInprocess[idx] : undefined;
    if (!prev || idx == null || idx < 0) return false;
    const goingOngoing = row.currentStatus === 'Ongoing';
    const nextRow = appendProcHistory(
      normalizeProcRow({
        ...prev,
        ...row,
        status: goingOngoing ? PROC_STATUS.ON_GOING : PROC_STATUS.IN_PROCESS,
        currentStatus: row.currentStatus || prev.currentStatus || 'In Process',
      }),
      {
        action: goingOngoing ? 'Moved to Ongoing Contract' : 'Updated In Process record',
        ...procHistoryActor(actor),
        from: PROC_STATUS.IN_PROCESS,
        to: goingOngoing ? PROC_STATUS.ON_GOING : PROC_STATUS.IN_PROCESS,
      },
    );
    if (goingOngoing) {
      procInprocess.splice(idx, 1);
      saveJson(STORAGE_KEYS.procInprocess, procInprocess);
      return get().promoteInProcessToOngoing(-1, nextRow, actor, procInprocess);
    }
    procInprocess[idx] = nextRow;
    saveJson(STORAGE_KEYS.procInprocess, procInprocess);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ procInprocess });
    return true;
  },

  promoteInProcessToOngoing: (index, row, actor, procInprocessOverride) => {
    const procInprocess = procInprocessOverride || [...get().procInprocess];
    let idx = index;
    if ((idx == null || idx < 0) && row?.id) {
      idx = procInprocess.findIndex((r) => r.id === row.id);
    }
    const prev = idx >= 0 ? procInprocess[idx] : row;
    if (!prev?.id && !row?.id) return false;
    const moved = appendProcHistory(
      normalizeProcRow({ ...prev, ...row, status: PROC_STATUS.ON_GOING, currentStatus: 'Ongoing' }),
      {
        action: 'Moved to Ongoing Contract',
        ...procHistoryActor(actor),
        from: PROC_STATUS.IN_PROCESS,
        to: PROC_STATUS.ON_GOING,
      },
    );
    const nextInprocess = idx >= 0
      ? procInprocess.filter((_, i) => i !== idx)
      : procInprocess.filter((r) => r.id !== moved.id);
    const procOngoing = [...get().procOngoing.filter((r) => r.id !== moved.id), moved];
    const cat = procCmCategory(moved.type);
    if (cat) {
      const contractMgmt = { ...get().contractMgmt, [cat]: [...get().contractMgmt[cat]] };
      const cmRow = buildCmRowFromProc(moved);
      if (cmRow) {
        const existingIdx = findCmRowIndex(contractMgmt[cat], cmRow);
        if (existingIdx >= 0) {
          contractMgmt[cat][existingIdx] = mergeCmRow(contractMgmt[cat][existingIdx], cmRow);
        } else {
          cmRow.sr = contractMgmt[cat].length + 1;
          contractMgmt[cat].push(cmRow);
        }
        saveJson(STORAGE_KEYS.contractMgmt, contractMgmt);
        set({ contractMgmt });
      }
    }
    saveJson(STORAGE_KEYS.procInprocess, nextInprocess);
    saveJson(STORAGE_KEYS.procOngoing, procOngoing);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    get().pushProcHistory({
      action: 'Moved to Ongoing Contract',
      packageId: moved.id,
      ref: moved.ref,
      type: moved.type,
      ...procHistoryActor(actor),
      from: PROC_STATUS.IN_PROCESS,
      to: PROC_STATUS.ON_GOING,
    });
    get().addAudit('Procurement', `${moved.ref} moved to Ongoing / Contract Mgmt`);
    set({ procInprocess: nextInprocess, procOngoing });
    return true;
  },

  completeProcFromCm: (procId, actor) => {
    const procOngoing = [...get().procOngoing];
    const idx = procOngoing.findIndex((r) => r.id === procId);
    if (idx < 0) return false;
    const prev = procOngoing[idx];
    const cmLink = findCmRowForProc(get().contractMgmt, prev);
    const completed = appendProcHistory(
      normalizeProcRow({
        ...prev,
        status: PROC_STATUS.COMPLETED,
        currentStatus: 'Completed',
        cmData: cmLink ? { cat: cmLink.cat, row: { ...cmLink.row } } : prev.cmData,
      }),
      {
        action: 'Completed via Contract Management',
        ...procHistoryActor(actor),
        from: PROC_STATUS.ON_GOING,
        to: PROC_STATUS.COMPLETED,
      },
    );
    procOngoing.splice(idx, 1);
    const procCompleted = [...get().procCompleted, completed];
    saveJson(STORAGE_KEYS.procOngoing, procOngoing);
    saveJson(STORAGE_KEYS.procCompleted, procCompleted);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    get().pushProcHistory({
      action: 'Completed',
      packageId: prev.id,
      ref: prev.ref,
      type: prev.type,
      ...procHistoryActor(actor),
      from: PROC_STATUS.ON_GOING,
      to: PROC_STATUS.COMPLETED,
    });
    get().addAudit('Contract Management', `${prev.ref} marked Completed`);
    set({ procOngoing, procCompleted });
    return true;
  },

  saveContractMgmt: (cat, index, row, actor) => {
    const contractMgmt = { ...get().contractMgmt, [cat]: [...get().contractMgmt[cat]] };
    const clean = stripCmRowMeta(row);
    let idx = index;
    if (idx == null || idx < 0 || !contractMgmt[cat][idx]
      || (clean.procId && contractMgmt[cat][idx]?.procId !== clean.procId)) {
      idx = findCmRowIndex(contractMgmt[cat], clean);
    }
    const merged = idx >= 0
      ? mergeCmRow(contractMgmt[cat][idx], clean)
      : mergeCmRow(null, clean);
    if (idx >= 0) contractMgmt[cat][idx] = merged;
    else contractMgmt[cat].push(merged);
    saveJson(STORAGE_KEYS.contractMgmt, contractMgmt);
    set({ contractMgmt });
    if (merged.status === 'Completed' && merged.procId) {
      get().completeProcFromCm(merged.procId, actor);
    }
  },

  importContractMgmtCategory: (cat, rows) => {
    const contractMgmt = { ...get().contractMgmt, [cat]: [...(get().contractMgmt[cat] || [])] };
    let added = 0;
    rows.forEach((row) => {
      const idx = findCmRowIndex(contractMgmt[cat], row);
      if (idx >= 0) {
        contractMgmt[cat][idx] = mergeCmRow(contractMgmt[cat][idx], row);
      } else {
        const next = { ...row, sr: contractMgmt[cat].length + 1 };
        contractMgmt[cat].push(next);
        added += 1;
      }
    });
    saveJson(STORAGE_KEYS.contractMgmt, contractMgmt);
    set({ contractMgmt });
    get().addAudit('Contract Management Import', `${cat}: ${rows.length} records (${added} new)`);
    return rows.length;
  },

  importTrainingComponent: (component, rows) => {
    let enriched = [1, 2].includes(component)
      ? enrichTrainingPrincipals(rows, SEED.TRAINING_PRINCIPALS)
      : rows;
    if (component === 1) enriched = enrichCoeTrainingRows(enriched);
    else if (component === 2) enriched = enrichMarketTrainingRows(enriched);
    const trainingByComponent = { ...get().trainingByComponent, [component]: enriched };
    saveJson(STORAGE_KEYS.training(component), enriched);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    get().addAudit('Training Import', `Component ${component}: ${enriched.length} batches imported`);
    set({ trainingByComponent });
    return enriched.length;
  },

  saveTrainingRow: (component, index, row) => {
    const trainingByComponent = { ...get().trainingByComponent };
    const rows = [...trainingByComponent[component]];
    if (index != null) rows[index] = row;
    else rows.push(row);
    let enriched = [1, 2].includes(component)
      ? enrichTrainingPrincipals(rows, SEED.TRAINING_PRINCIPALS)
      : rows;
    if (component === 1) enriched = enrichCoeTrainingRows(enriched);
    else if (component === 2) enriched = enrichMarketTrainingRows(enriched);
    trainingByComponent[component] = enriched;
    saveJson(STORAGE_KEYS.training(component), enriched);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ trainingByComponent });
  },

  importC1PlacementRecords: (rows) => {
    const c1PlacementRecords = rows.map((row, i) => normalizePlacementRecord(row, i));
    saveJson(STORAGE_KEYS.c1Placement, c1PlacementRecords);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    get().addAudit('Training Import', `Component I placement: ${c1PlacementRecords.length} trainee records imported`);
    set({ c1PlacementRecords });
    return c1PlacementRecords.length;
  },

  saveC1PlacementRow: (index, row) => {
    const c1PlacementRecords = [...get().c1PlacementRecords];
    const normalized = normalizePlacementRecord(row, index != null && index >= 0 ? index : c1PlacementRecords.length);
    if (index != null && index >= 0) c1PlacementRecords[index] = normalized;
    else c1PlacementRecords.push(normalized);
    saveJson(STORAGE_KEYS.c1Placement, c1PlacementRecords);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ c1PlacementRecords });
  },

  importC2PlacementRecords: (rows) => {
    const c2PlacementRecords = rows.map((row, i) => normalizePlacementRecord(row, i));
    saveJson(STORAGE_KEYS.c2Placement, c2PlacementRecords);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    get().addAudit('Training Import', `Component II placement: ${c2PlacementRecords.length} trainee records imported`);
    set({ c2PlacementRecords });
    return c2PlacementRecords.length;
  },

  saveC2PlacementRow: (index, row) => {
    const c2PlacementRecords = [...get().c2PlacementRecords];
    const normalized = normalizePlacementRecord(row, index != null && index >= 0 ? index : c2PlacementRecords.length);
    if (index != null && index >= 0) c2PlacementRecords[index] = normalized;
    else c2PlacementRecords.push(normalized);
    saveJson(STORAGE_KEYS.c2Placement, c2PlacementRecords);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ c2PlacementRecords });
  },

  saveGap: (index, row) => {
    const gap = [...get().gap];
    if (index != null) gap[index] = row;
    else gap.push(row);
    saveJson(STORAGE_KEYS.gap, gap);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ gap });
  },

  deleteGap: (index) => {
    const gap = [...get().gap];
    if (index == null || index < 0 || index >= gap.length) return;
    gap.splice(index, 1);
    saveJson(STORAGE_KEYS.gap, gap);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ gap });
  },

  saveGapAlert: (message, updatedBy = 'Gender Specialist') => {
    const gapAlert = {
      message: String(message || '').trim(),
      updatedAt: new Date().toLocaleString('en-PK', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      updatedBy: updatedBy || 'Gender Specialist',
    };
    saveJson(STORAGE_KEYS.gapAlert, gapAlert);
    get().addAudit('Gender Action Plan', gapAlert.message
      ? `Notification updated: ${gapAlert.message.slice(0, 80)}`
      : 'Notification cleared');
    set({ gapAlert });
  },

  saveBudgetRow: (index, row) => {
    const budgetRows = [...get().budgetRows];
    if (index != null) budgetRows[index] = row;
    else budgetRows.push(row);
    saveJson(STORAGE_KEYS.budget, budgetRows);
    set({ budgetRows });
  },

  setAccountsTab: (tab) => set({ accountsTab: tab }),

  persistAccounts: (accounts) => {
    saveJson(STORAGE_KEYS.accounts, accounts);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ accounts });
  },

  setAccountsFinancialYear: (year) => {
    const accounts = { ...get().accounts, financialYear: year, fySelected: !!year };
    get().persistAccounts(accounts);
  },

  saveAccountsEntry: (year, accountType, id, patch) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    const bucket = accounts.accountsByYear?.[year]?.[accountType];
    if (!bucket?.entries) return;
    const idx = bucket.entries.findIndex((e: Record<string, unknown>) => e.id === id);
    if (idx < 0) return;
    bucket.entries[idx] = { ...bucket.entries[idx], ...patch };
    Object.assign(bucket, recalcAccountTotals(bucket.entries));
    get().persistAccounts(accounts);
  },

  addAccountsEntry: (year, accountType, entry) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    if (!accounts.accountsByYear) accounts.accountsByYear = {};
    if (!accounts.accountsByYear[year]) {
      accounts.accountsByYear[year] = {
        RFA: { entries: [], fundsReleased: { pkr: 0, usd: 0 }, fundsUtilization: { pkr: 0, usd: 0 } },
        AAA: { entries: [], fundsReleased: { pkr: 0, usd: 0 }, fundsUtilization: { pkr: 0, usd: 0 } },
        PASSBOOK: { entries: [], fundsReleased: { pkr: 0, usd: 0 }, fundsUtilization: { pkr: 0, usd: 0 } },
        DDO: { entries: [], fundsReleased: { pkr: 0, usd: 0 }, fundsUtilization: { pkr: 0, usd: 0 } },
      };
    }
    if (!accounts.accountsByYear[year][accountType]) {
      accounts.accountsByYear[year][accountType] = {
        entries: [],
        fundsReleased: { pkr: 0, usd: 0 },
        fundsUtilization: { pkr: 0, usd: 0 },
      };
    }
    const bucket = accounts.accountsByYear[year][accountType];
    if (!bucket) return;
    const id = String(entry.id || `cb-${Date.now()}`);
    bucket.entries = bucket.entries || [];
    bucket.entries.push({
      ...entry,
      id,
      financialYear: year,
    });
    Object.assign(bucket, recalcAccountTotals(bucket.entries));
    get().persistAccounts(accounts);
    get().addAudit('Accounts', `Cashbook entry added (${accountType})`);
  },

  deleteAccountsEntry: (year, accountType, id) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    const bucket = accounts.accountsByYear?.[year]?.[accountType];
    if (!bucket?.entries) return;
    bucket.entries = bucket.entries.filter((e: Record<string, unknown>) => e.id !== id);
    Object.assign(bucket, recalcAccountTotals(bucket.entries));
    get().persistAccounts(accounts);
    get().addAudit('Accounts', `Cashbook entry deleted (${accountType})`);
  },

  saveAccountsHoa: (accountType, hoa) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    const list = accounts.hoas?.[accountType] || [];
    const num = (v: unknown) => (v === '' || v == null ? 0 : Number(v) || 0);
    const code = String(hoa.code ?? '').trim();
    const name = String(hoa.name ?? '').trim();
    if (hoa.isSub && hoa.parentId) {
      const parent = list.find((h: Record<string, unknown>) => h.id === hoa.parentId);
      if (!parent) return;
      const subHeads = (parent.subHeads as Array<Record<string, unknown>>) || [];
      parent.subHeads = subHeads;
      const sid = String(hoa.id || `sub-${Date.now()}`);
      const idx = subHeads.findIndex((s) => s.id === sid);
      const rec = {
        id: sid,
        code,
        name,
        budget2324: num(hoa.budget2324),
        budget2425: num(hoa.budget2425),
        revised2425: num(hoa.revised2425),
        utilization2425: num(hoa.utilization2425),
        balance2425: num(hoa.balance2425),
      };
      if (idx >= 0) subHeads[idx] = { ...subHeads[idx], ...rec };
      else subHeads.push(rec);
    } else {
      const id = String(hoa.id || `hoa-${Date.now()}`);
      const idx = list.findIndex((h: Record<string, unknown>) => h.id === id);
      const rec = {
        id,
        code,
        name,
        accountType,
        subHeads: (hoa.subHeads as unknown[]) || [],
        budget2324: num(hoa.budget2324),
        budget2425: num(hoa.budget2425),
        revised2425: num(hoa.revised2425),
        utilization2425: num(hoa.utilization2425),
        balance2425: num(hoa.balance2425),
        amountPkr: num(hoa.amountPkr),
        amountUsd: num(hoa.amountUsd),
      };
      if (idx >= 0) list[idx] = { ...list[idx], ...rec };
      else list.push(rec);
      if (!accounts.hoas) accounts.hoas = {};
      accounts.hoas[accountType] = list;
    }
    get().persistAccounts(accounts);
  },

  deleteAccountsHoa: (accountType, id) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    const list = accounts.hoas?.[accountType] || [];
    const headIdx = list.findIndex((h: Record<string, unknown>) => h.id === id);
    if (headIdx >= 0) {
      list.splice(headIdx, 1);
    } else {
      list.forEach((h: Record<string, unknown>) => {
        const subHeads = (h.subHeads as Array<Record<string, unknown>>) || [];
        h.subHeads = subHeads.filter((s) => s.id !== id);
      });
    }
    if (!accounts.hoas) accounts.hoas = {};
    accounts.hoas[accountType] = list;
    get().persistAccounts(accounts);
  },

  saveAccountsParticular: (accountType, particular) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    const list = (accounts.particulars?.[accountType] as Array<Record<string, unknown>>) || [];
    const name = String(particular.name ?? '').trim();
    if (!name) return;
    const id = String(particular.id || `part-${accountType}-${Date.now()}`);
    const idx = list.findIndex((item) => item.id === id);
    const rec = { id, name, accountType };
    if (idx >= 0) list[idx] = { ...list[idx], ...rec };
    else list.push(rec);
    if (!accounts.particulars) accounts.particulars = {};
    accounts.particulars[accountType] = list;
    get().persistAccounts(accounts);
    get().addAudit('Accounts', `Particular saved (${accountType})`);
  },

  deleteAccountsParticular: (accountType, id) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    const list = (accounts.particulars?.[accountType] as Array<Record<string, unknown>>) || [];
    if (!accounts.particulars) accounts.particulars = {};
    accounts.particulars[accountType] = list.filter((item) => item.id !== id);
    get().persistAccounts(accounts);
    get().addAudit('Accounts', `Particular deleted (${accountType})`);
  },

  importAccountsCashbook: (year, parsed) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    if (!accounts.accountsByYear) accounts.accountsByYear = {};
    if (!accounts.accountsByYear[year]) {
      accounts.accountsByYear[year] = {
        RFA: { entries: [], fundsReleased: { pkr: 0, usd: 0 }, fundsUtilization: { pkr: 0, usd: 0 } },
        AAA: { entries: [], fundsReleased: { pkr: 0, usd: 0 }, fundsUtilization: { pkr: 0, usd: 0 } },
        PASSBOOK: { entries: [], fundsReleased: { pkr: 0, usd: 0 }, fundsUtilization: { pkr: 0, usd: 0 } },
        DDO: { entries: [], fundsReleased: { pkr: 0, usd: 0 }, fundsUtilization: { pkr: 0, usd: 0 } },
      };
    }
    const byYear = accounts.accountsByYear;
    ['RFA', 'AAA', 'PASSBOOK', 'DDO'].forEach((type) => {
      const bucket = parsed[type];
      if (bucket?.entries?.length) {
        byYear[year][type] = bucket;
      }
    });
    get().persistAccounts(accounts);
    get().addAudit('Accounts', `Cashbook imported for FY ${year}`);
  },

  importAccountsFinancialStatement: (year, data) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    accounts.financialStatements = accounts.financialStatements || {};
    accounts.financialStatements[year] = data;
    get().persistAccounts(accounts);
    get().addAudit('Accounts', `Financial statement imported for FY ${year}`);
  },

  saveBankStatement: (year, entries) => {
    const accounts = JSON.parse(JSON.stringify(get().accounts)) as AccountsState;
    accounts.bankStatements = accounts.bankStatements || {};
    accounts.bankStatements[year] = entries;
    get().persistAccounts(accounts);
  },

  saveComponent3: (data) => {
    const merged = mergeComponent3(data);
    saveJson(STORAGE_KEYS.component3, merged);
    saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);
    set({ component3: merged });
  },

  saveC3DeliverableDetail: (index, row) => {
    const c3 = { ...get().component3 };
    const deliverableDetails = [...((c3.deliverableDetails as Record<string, unknown>[]) || [])];
    if (index != null) deliverableDetails[index] = row;
    else deliverableDetails.push(row);
    get().saveComponent3({ ...c3, deliverableDetails });
  },

  saveC3DeliverablePayment: (index, row) => {
    const c3 = { ...get().component3 };
    const deliverables = [...((c3.deliverables as Record<string, unknown>[]) || [])];
    const rec = c3RecalcDeliverable({ ...row });
    if (index != null) deliverables[index] = rec;
    else deliverables.push(rec);
    get().saveComponent3({ ...c3, deliverables });
  },

  saveC3Expense: (index, row) => {
    const c3 = { ...get().component3 };
    const expenses = [...((c3.expenses as Record<string, unknown>[]) || [])];
    const rec = c3RecalcExpense({ ...row, months: { ...((row.months) || {}) } });
    if (index != null) expenses[index] = rec;
    else expenses.push(rec);
    get().saveComponent3({ ...c3, expenses });
  },

  saveC3Contingency: (index, row) => {
    const c3 = { ...get().component3 };
    const contingencies = [...((c3.contingencies as Record<string, unknown>[]) || [])];
    const rec = {
      sr: Number(row.sr) || (index != null ? index + 1 : contingencies.length + 1),
      contingencyAmount: Number(row.contingencyAmount) || 0,
      description: String(row.description || '').trim(),
      invoiceDate: String(row.invoiceDate || '').trim(),
      invoiceValue: Number(row.invoiceValue ?? row.amount) || 0,
      status: String(row.status || 'Unpaid'),
      remarks: String(row.remarks || '').trim(),
      letterRef: String(row.letterRef || '').trim(),
      letterDate: String(row.letterDate || '').trim(),
      approvalDate: String(row.approvalDate || '').trim(),
    };
    if (index != null) contingencies[index] = rec;
    else contingencies.push(rec);
    get().saveComponent3({ ...c3, contingencies });
  },

  saveC3ProvisionalSum: (index, row) => {
    const c3 = { ...get().component3 };
    const provisionalSums = [...((c3.provisionalSums as Record<string, unknown>[]) || [])];
    const rec = {
      description: String(row.description || '').trim(),
      invoiceDate: String(row.invoiceDate || '').trim(),
      invoiceValue: Number(row.invoiceValue) || 0,
      status: String(row.status || 'Unpaid'),
      remarks: String(row.remarks || '').trim(),
      letterRef: String(row.letterRef || '').trim(),
      letterDate: String(row.letterDate || '').trim(),
      approvalDate: String(row.approvalDate || '').trim(),
    };
    if (index != null) provisionalSums[index] = rec;
    else provisionalSums.push(rec);
    get().saveComponent3({ ...c3, provisionalSums });
  },

  addAudit: (type, desc) => {
    const entry = {
      type,
      desc,
      by: 'PIU Admin',
      time: new Date().toLocaleString(),
      status: 'OK',
    };
    const auditLog = [entry, ...get().auditLog].slice(0, 50);
    saveJson(STORAGE_KEYS.audit, auditLog);
    set({ auditLog });
  },

  openModal: (modal) => set({ modal }),
  closeModal: () => set({ modal: null }),
}));
